import { PropValidator, RenderContext } from 'vue/types/options'
import Vue from 'vue'
import { LocaleMessage } from 'vue-i18n'
import { IDevice, IVW } from '@/types/model'
import { Preset } from '@/types/api'

// =======================================================
// Vuetify Theme
// =======================================================
export const vuetifyThemeAware = Vue.extend({
  inject: ['theme'],  // // See https://github.com/vuetifyjs/vuetify/issues/5223
  props: {
    dark: {
      type: Boolean,
      default: null
    } as PropValidator<boolean | null>,
    light: {
      type: Boolean,
      default: null
    } as PropValidator<boolean | null>
  },
  computed: {
    isDark (): boolean {
      if (this.dark === true) {
        // explicitly dark
        return true
      } else if (this.light === true) {
        // explicitly light
        return false
      } else {
        // inherit from parent, or default false if there is none
        return this.$vuetify.dark || this['theme']['isDark']
      }
    },
    stdBgClr (): string {
      return this.isDark ? '#333' : '#fff'
    }
  }
})


// =======================================================
// HTML element width watcher
// =======================================================
export const elementWidth = Vue.extend({
  data (): {
    elementWidthDict: {[refName: string]: number},
  } {
    return {
      elementWidthDict: {},
    }
  },
  methods: {
    getElementByRefName (refName: string): null | Element {
      const ref = this.$refs[refName]
      let el: Element
      if (!ref) {
        console.error('[To Developer] watchElementWidth(): not found ref.')
        return null
      }
      if (ref instanceof Element) {
        el = ref
      } else {  // if a Vue Component
        el = ref['$el'] as Element
      }
      return el
    },
    watchElementWidth (refName: string, updateWhenVmDataChanged?: string[]): boolean {
      this.$set(this.elementWidthDict, refName, 1)
      if (updateWhenVmDataChanged) {
        for (const key of updateWhenVmDataChanged) {
          this.$watch(key, this.updateElementWidthDict)
        }
      }
      return true
    },
    updateElementWidthDict () {
      this.$nextTick(() => {
        for (const refName in this.elementWidthDict) {
          // this.vwPreviewWidth = el.clientWidth  // NOTE: This will include padding
          const el = this.getElementByRefName(refName)
          if (!el) { console.log('continue'); continue }
          const style = window.getComputedStyle(el)
          const rect = el.getBoundingClientRect()
          console.log('[mixin] elementWidth', refName, rect, style.getPropertyValue('padding-left'))
          const width = el.clientWidth - (parseFloat(style.getPropertyValue('padding-left')) + parseFloat(style.getPropertyValue('padding-right')))
          this.$set(this.elementWidthDict, refName, width)
        }
      })
    },
    getElementWidth (refName: string): number {  // DEPRECATED: Seems unneessary
      console.log('getElementWidth() ==>', refName,  this.elementWidthDict[refName])
      return this.elementWidthDict[refName] || 1
    },
  },
  mounted () {
    this.updateElementWidthDict()
    setTimeout(() => { this.updateElementWidthDict() }, 100)  // FIXME: dirty workaround
  },
  watch: {
    '$state.common.windowSize.width' (nv: number) {
      this.updateElementWidthDict()
    }
  }
})
// =======================================================
// general dialog
// =======================================================
export const generalDialogMixin = Vue.extend({
  data: function (): {
    dialog: {
      show: boolean,
      /** workaround for shitty vue-router + Vuetify dialog */
      showDelay: number
    }
  } {
    return {
      dialog: {
        show: false,
        showDelay: 0
      }
    }
  },
  methods: {
    openThisDialog () {
      this.dialog.show = true
    },
    closeThisDialog () {
      this.$router.go(-1)
      console.log('close')
    },
  },
  mounted () {
    if (this.$s.$state.device.firstTimeFetched) {
      console.log('直接開', this.$vnode.componentOptions ? this.$vnode.componentOptions.tag : '', this)
      this.openThisDialog()
    } else {
      console.log('延遲開', this.$vnode.componentOptions ? this.$vnode.componentOptions.tag : '')
      const unwatch = this.$watch(
        () => this.$s.$state.device.firstTimeFetched,
        (newVal) => {
          if (newVal) {
            if (this.dialog.showDelay !== 0) {
              setTimeout(() => {
                this.openThisDialog()
              }, this.dialog.showDelay)
            } else {
              this.openThisDialog()
            }
            unwatch()
          }
        }
      )
    }
  }
})

// =======================================================
// DeviceDialog (master)
// =======================================================
export const masterDeviceDialogMixin = Vue.extend({
  data: function (): {
    dialog: {
      show: boolean
      device: IDevice | null
    }
  } {
    return {
      dialog: {
        show: false,
        device: null
      }
    }
  },
  methods: {
    /** This will open the dialog for device, whose MAC address is $route.params.master_mac */
    openThisDialog () {
      const mac = this.$route.params.master_mac  // [DIFF] Get MAC from router (URL param)
      const dev = this.$s.$state.device.list.find((d) => d.mac === mac)
      if (dev) {
        this.dialog.device = dev
        this.dialog.show = true
      } else {
        this.$router.push({ name: 'home' })
        console.error('[Error] Not existed page, redirect to home')
      }
    },
    /** close this dialog & recover the router history. */
    closeThisDialog () {
      this.$router.go(-1)
      console.log('close')
    },
  },
  mounted () {
    if (this.$s.$state.device.firstTimeFetched) {
      this.openThisDialog()
    } else {
      const unwatch = this.$watch(
        () => this.$s.$state.device.firstTimeFetched,
        (newVal) => {
          if (newVal) {
            this.openThisDialog()
            unwatch()
          }
        }
      )
    }
  }
})

// =======================================================
// DeviceDialog (slave)
// =======================================================
export const slaveDeviceDialogMixin = Vue.extend({
  data: function (): {
    dialog: {
      /** Don't manually modify this value, call closeThisDialog() instead */
      show: boolean
      device: IDevice | null
    }
  } {
    return {
      dialog: {
        show: false,
        device: null
      }
    }
  },
  methods: {
    openThisDialog () {
      const mac = this.$route.params.mac  // [DIFF] Get MAC from router (URL param)
      const dev = this.$s.$state.device.list.find((d) => d.mac === mac)
      if (dev) {
        this.dialog.device = dev
        this.dialog.show = true
      } else {
        this.$router.push({ name: 'home' })
      }
    },
    /** close this dialog & recover the router history. */
    closeThisDialog () {
      this.$router.go(-1)
      console.log('close')
    }
  },
  mounted () {
    console.log('mixin minor mounted')
    if (this.$s.$state.device.firstTimeFetched) {
      this.openThisDialog()
    } else {
      const unwatch = this.$watch(
        () => this.$s.$state.device.firstTimeFetched,
        (newVal) => {
          if (newVal) {
            this.openThisDialog()
            unwatch()
          }
        }
      )
    }
  }
})

// =======================================================
// VW Dialog (slave)
// =======================================================
export const vwDialogMixin = Vue.extend({
  data: function (): {
    dialog: {
      show: boolean
      vw: IVW | null,
      /** Used by closeThisDialogAfterConfirm */
      dirty: boolean,
    }
  } {
    return {
      dialog: {
        show: false,
        vw: null,
        dirty: false,
      }
    }
  },
  methods: {
    /** This will open the dialog for vw, whose id is $route.params.vwid */
    openThisDialog () {
      const vwid: number = ~~this.$route.params.vwid
      const vw = this.$s.device.getters.vwList.find((v) => v.id === vwid)
      console.log('vw ===', vwid, vw)
      if (vw) {
        this.dialog.vw = vw
        this.dialog.show = true
      } else {
        this.$router.push({ name: 'vwlst' })
        console.error('[Error] Not existed page, redirect to home')
      }
    },
    /** close this dialog & recover the router history. */
    closeThisDialog () {
      this.$router.go(-1)
      console.log('close')
    },
  },
  mounted () {
    if (this.$s.$state.device.firstTimeFetched) {
      this.openThisDialog()
    } else {
      const unwatch = this.$watch(
        () => this.$s.$state.device.firstTimeFetched,
        (newVal) => {
          if (newVal) {
            this.openThisDialog()
            unwatch()
          }
        }
      )
    }
  }
})

// =======================================================
// VW Dialog (slave)
// =======================================================
export const presetDialogMixin = Vue.extend({
  data: function (): {
    dialog: {
      show: boolean
      preset: Preset | null,
      /** Used by closeThisDialogAfterConfirm */
      dirty: boolean,
    }
  } {
    return {
      dialog: {
        show: false,
        preset: null,
        dirty: false,
      }
    }
  },
  methods: {
    /** This will open the dialog for vw, whose id is $route.params.vwid */
    openThisDialog () {
      const presetid: number = ~~this.$route.params.presetid
      const preset = this.$state.device.presetList.find((p) => p.id === presetid)
      console.log('preset ===', presetid, preset)
      if (preset) {
        this.dialog.preset = preset
        this.dialog.show = true
      } else {
        this.$router.push({ name: 'preset' })
        console.error('[Error] Not existed page, redirect to home')
      }
    },
    /** close this dialog & recover the router history. */
    closeThisDialog () {
      this.$router.go(-1)
      console.log('close')
    },
  },
  mounted () {
    if (this.$s.$state.device.firstTimeFetched) {
      this.openThisDialog()
    } else {
      const unwatch = this.$watch(
        () => this.$s.$state.device.firstTimeFetched,
        (newVal) => {
          if (newVal) {
            this.openThisDialog()
            unwatch()
          }
        }
      )
    }
  }
})
// ==================================================
// is desktop or mobile
// ==================================================
export const isDesktopMixin = Vue.extend({
  computed: {
    isDesktop (): boolean { return this.$s.$state.common.windowSize.width > 800 }
  }
})
// ==================================================
// Thumbnail Size
// ==================================================
